if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.abortController || !window.ScoreggiaMediaCore?.CircularCarousel) return;
        this.abortController = new AbortController();
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.viewport = this.querySelector('[data-scoreggia-product-viewport]');
        this.zoomMinimumScale = 1;
        this.zoomMaximumScale = 5;
        this.zoomInitialScale = window.ScoreggiaMediaCore.clampScale(
          Number.parseFloat(this.dataset.scoreggiaZoomLevel || '2'),
          this.zoomMinimumScale,
          this.zoomMaximumScale,
        );
        this.zoomGestureOutcome = window.ScoreggiaMediaCore.zoomGestureOutcome;
        this.zoomScale = this.zoomInitialScale;
        this.zoomed = false;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomBounds = null;
        this.zoomCenteredBounds = null;
        this.zoomSourceBounds = null;
        this.zoomSourceSlide = null;
        this.zoomSourceMediaId = null;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.zoomPointers = new Map();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomManipulated = false;
        this.activeZoomPointerListeners = null;
        this.zoomSourceImage = null;
        this.zoomSourceButton = null;
        this.suppressNextZoomClick = false;
        this.zoomCloseTimer = null;
        this.zoomClickTimer = null;
        this.zoomAnnouncementTimer = null;
        this.zoomOpenFrame = null;
        this.zoomTransformFrame = null;
        this.zoomWheelFrame = null;
        this.zoomWheelIdleTimer = null;
        this.zoomWheelSamples = [];
        this.inViewport = false;
        this.hovering = false;
        this.focusWithin = false;
        this.quickAdd = Boolean(this.closest('quick-add-modal'));
        this.productPage =
          this.dataset.scoreggiaProductPage === 'true' &&
          !this.quickAdd &&
          Boolean(document.querySelector('main[data-template="product"]')?.contains(this));
        this.zoomEnabled = this.productPage && this.dataset.scoreggiaInlineZoom === 'true';
        this.autoplayEnabled = this.productPage && this.dataset.scoreggiaAutoplay === 'true';
        this.autoplayInterval = Math.max(3000, Number.parseInt(this.dataset.scoreggiaAutoplayInterval || '6000', 10));
        this.autoplay = new window.ScoreggiaMediaCore.AutoplayState();

        this.controller = new window.ScoreggiaMediaCore.CircularCarousel(this, {
          trackSelector: '[data-scoreggia-product-track]',
          viewportSelector: '[data-scoreggia-product-viewport]',
          slideSelector: '[data-scoreggia-product-slide]',
          thumbnailSelector: '[data-scoreggia-product-thumbnail]',
          previousSelector: '[data-scoreggia-product-previous]',
          nextSelector: '[data-scoreggia-product-next]',
          beforeChange: (nextIndex, previousIndex) => {
            if (nextIndex !== previousIndex) this.resetZoom({ immediate: true });
          },
          afterChange: (nextIndex, previousIndex, options) => this.handleMediaChange(nextIndex, previousIndex, options),
          onInteraction: (source) => this.stopAutoplayAfterInteraction(source),
          isZoomed: () => this.zoomed,
          onEscape: () => {
            if (!this.zoomed) return false;
            this.resetZoom({ restoreFocus: true });
            return true;
          },
        });
        this.controller.connect();
        this.configureZoomControls();
        if (this.zoomEnabled) this.ensureZoomLayer();
        this.bindEvents();
        this.observeVisibility();
      }

      disconnectedCallback() {
        this.resetZoom({ immediate: true });
        this.removeZoomLayer();
        this.controller?.disconnect();
        this.controller = null;
        this.autoplay?.clear();
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
        this.abortController?.abort();
        this.abortController = null;
      }

      bindEvents() {
        const { signal } = this.abortController;
        this.addEventListener(
          'click',
          (event) => {
            const zoomButton = event.target.closest('[data-scoreggia-zoom]');
            if (zoomButton && this.zoomEnabled && !zoomButton.hidden) {
              event.preventDefault();
              this.stopAutoplayAfterInteraction('zoom');
              this.setZoom(!this.zoomed);
              return;
            }
            if (event.target.closest('[data-scoreggia-native-media], video, model-viewer, iframe')) {
              this.stopAutoplayAfterInteraction('native-media');
            }
          },
          { signal },
        );
        this.addEventListener(
          'pointerdown',
          (event) => {
            if (event.target.closest('[data-scoreggia-native-media], video, model-viewer, iframe')) {
              this.stopAutoplayAfterInteraction('native-media');
            }
          },
          { signal },
        );
        this.addEventListener(
          'focusin',
          (event) => {
            this.focusWithin = true;
            this.autoplay.clear();
            if (
              event.target.closest(
                '[data-scoreggia-media-control], [data-scoreggia-product-thumbnail], [data-scoreggia-zoom], [data-scoreggia-native-media], video, model-viewer, iframe',
              )
            ) {
              this.stopAutoplayAfterInteraction('focus');
            }
          },
          { signal },
        );
        this.addEventListener(
          'focusout',
          () => {
            window.setTimeout(() => {
              this.focusWithin = this.contains(document.activeElement);
              this.scheduleAutoplay();
            });
          },
          { signal },
        );
        this.addEventListener(
          'pointerenter',
          () => {
            if (!window.matchMedia('(hover: hover)').matches) return;
            this.hovering = true;
            this.autoplay.clear();
          },
          { signal },
        );
        this.addEventListener(
          'pointerleave',
          () => {
            this.hovering = false;
            this.scheduleAutoplay();
          },
          { signal },
        );
        document.addEventListener(
          'visibilitychange',
          () => {
            if (document.hidden) this.autoplay.clear();
            else this.scheduleAutoplay();
          },
          { signal },
        );
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Escape' || !this.zoomed) return;
            event.preventDefault();
            this.stopAutoplayAfterInteraction('escape');
            this.resetZoom({ restoreFocus: true });
          },
          { signal },
        );
        window.addEventListener(
          'resize',
          () => {
            if (this.zoomed) this.refreshZoomBounds();
          },
          { signal },
        );
        window.addEventListener('orientationchange', () => this.refreshZoomBounds(), { signal });
        window.visualViewport?.addEventListener('resize', () => this.refreshZoomBounds(), { signal });
        window.visualViewport?.addEventListener('scroll', () => this.refreshZoomBounds(), { signal });
        window.addEventListener('pagehide', () => this.resetZoom({ immediate: true }), { signal });
      }

      observeVisibility() {
        if (!this.autoplayEnabled || this.controller.slides.length < 2) return;
        if (!('IntersectionObserver' in window)) {
          this.inViewport = true;
          this.scheduleAutoplay();
          return;
        }
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.inViewport = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.15);
            if (this.inViewport) this.scheduleAutoplay();
            else this.autoplay.clear();
          },
          { threshold: [0, 0.15, 0.5] },
        );
        this.intersectionObserver.observe(this);
      }

      scheduleAutoplay() {
        const allowed =
          this.autoplayEnabled &&
          this.controller?.slides.length > 1 &&
          !this.autoplay.userInteracted &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
          !document.hidden &&
          this.inViewport &&
          !this.hovering &&
          !this.focusWithin &&
          !this.zoomed &&
          !this.activeMediaBusy;
        this.autoplay.arm(
          () => this.controller?.move(1, { source: 'autoplay' }),
          this.autoplayInterval,
          allowed,
        );
      }

      stopAutoplayAfterInteraction() {
        this.autoplay.stopByUser();
      }

      handleMediaChange(nextIndex, previousIndex, options) {
        this.pauseInactiveMedia(nextIndex);
        const activeSlide = this.controller.slides[nextIndex];
        this.viewer?.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: { currentElement: activeSlide },
          }),
        );
        if (options.source !== 'initialize' && activeSlide) this.announceMedia(activeSlide, nextIndex + 1);
        this.scheduleAutoplay();
      }

      pauseInactiveMedia(activeIndex) {
        this.controller.slides.forEach((slide, index) => {
          if (index === activeIndex) return;
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('.js-youtube').forEach((video) => {
            video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          });
          slide.querySelectorAll('.js-vimeo').forEach((video) => {
            video.contentWindow?.postMessage('{"method":"pause"}', '*');
          });
          slide.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause?.());
        });
      }

      announceMedia(activeSlide, position) {
        if (!this.liveRegion) return;
        const image = activeSlide.querySelector('img');
        const announce = () => {
          this.liveRegion.setAttribute('aria-hidden', 'false');
          this.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          window.setTimeout(() => this.liveRegion?.setAttribute('aria-hidden', 'true'), 2000);
        };
        if (!image || image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      setActiveMedia(mediaId) {
        this.stopAutoplayAfterInteraction('variant');
        this.resetZoom({ immediate: true });
        this.controller.refresh();
        const nextIndex = this.controller.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        this.controller.goTo(nextIndex >= 0 ? nextIndex : 0, { immediate: true, source: 'variant' });
        this.preventStickyHeader();
      }

      setInitialHandoffMedia(mediaId) {
        this.resetZoom({ immediate: true });
        this.controller.refresh();
        const nextIndex = this.controller.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (nextIndex < 0) return false;
        this.controller.goTo(nextIndex, {
          immediate: true,
          scrollThumbnail: false,
          source: 'handoff',
        });
        this.scheduleAutoplay();
        return true;
      }

      prepareForMediaMutation() {
        this.resetZoom({ immediate: true });
        this.controller?.prepareForMutation();
      }

      setActiveThumbnail(thumbnail) {
        if (!thumbnail) return;
        const target = thumbnail.dataset.target;
        const nextIndex = this.controller.slides.findIndex((slide) => slide.dataset.mediaId === target);
        if (nextIndex >= 0) this.controller.goTo(nextIndex, { source: 'thumbnail' });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      }

      configureZoomControls() {
        this.querySelectorAll('[data-scoreggia-zoom]').forEach((button) => {
          if (this.zoomEnabled) return;
          button.hidden = true;
          button.disabled = true;
          button.setAttribute('aria-hidden', 'true');
        });
      }

      ensureZoomLayer() {
        if (this.zoomRoot?.isConnected) return;
        const staleRoot = document.querySelector('[data-scoreggia-elevated-zoom]');
        if (staleRoot) staleRoot.remove();

        const root = document.createElement('div');
        const dismissalLayer = document.createElement('div');
        const image = document.createElement('img');
        root.className = 'scoreggia-elevated-zoom';
        root.dataset.scoreggiaElevatedZoom = '';
        root.hidden = true;
        dismissalLayer.className = 'scoreggia-elevated-zoom__dismissal';
        dismissalLayer.dataset.scoreggiaZoomDismissal = '';
        dismissalLayer.setAttribute('aria-hidden', 'true');
        image.className = 'scoreggia-elevated-zoom__image';
        image.dataset.scoreggiaZoomImage = '';
        image.alt = '';
        image.draggable = false;
        image.setAttribute('aria-hidden', 'true');
        root.append(dismissalLayer, image);
        document.body.append(root);
        this.zoomRoot = root;
        this.zoomDismissalLayer = dismissalLayer;
        this.zoomImage = image;

        const { signal } = this.abortController;
        dismissalLayer.addEventListener('click', (event) => this.handleZoomDismissal(event), { signal });
        image.addEventListener('pointerdown', (event) => this.handleZoomPointerDown(event), { signal });
        image.addEventListener('wheel', (event) => this.handleZoomWheel(event), { passive: false, signal });
        image.addEventListener('lostpointercapture', (event) => {
          const pointer = this.zoomPointers.get(event.pointerId);
          if (pointer) pointer.captured = false;
        }, { signal });
        image.addEventListener('click', (event) => this.handleZoomImageClick(event), { signal });
        image.addEventListener('load', () => {
          if (this.zoomed) this.refreshZoomBounds();
        }, { signal });
        image.addEventListener('error', () => {
          if (this.zoomed) this.resetZoom({ immediate: true, restoreFocus: true });
        }, { signal });
        image.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      }

      removeZoomLayer() {
        this.finishZoomClose();
        this.zoomRoot?.remove();
        this.zoomRoot = null;
        this.zoomDismissalLayer = null;
        this.zoomImage = null;
      }

      setZoom(enabled) {
        if (!this.zoomEnabled) return;
        const activeSlide = this.controller.slides[this.controller.activeIndex];
        const zoomButton = activeSlide?.querySelector('[data-scoreggia-zoom]');
        const zoomImage = activeSlide?.querySelector('.scoreggia-inline-zoom__image');
        if (!zoomButton || !zoomImage || activeSlide?.hasAttribute('data-scoreggia-loop-clone')) return;

        this.resetZoom({ immediate: true });
        if (!enabled) return;
        this.ensureZoomLayer();
        document.documentElement.classList.add('scoreggia-elevated-zoom-open');
        const renderedBounds = this.getRenderedImageBounds(zoomImage);
        const centeredBounds = this.getCenteredZoomBounds(renderedBounds);
        if (!renderedBounds || !centeredBounds || !this.zoomRoot || !this.zoomImage) {
          document.documentElement.classList.remove('scoreggia-elevated-zoom-open');
          return;
        }
        this.zoomed = true;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomSourceBounds = renderedBounds;
        this.zoomCenteredBounds = centeredBounds;
        this.zoomBounds = centeredBounds;
        this.zoomSourceSlide = activeSlide;
        this.zoomSourceMediaId = activeSlide.dataset.mediaId || null;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.zoomSourceImage = zoomImage;
        this.zoomSourceButton = zoomButton;
        this.classList.add('is-zoomed');
        activeSlide.classList.add('is-zoomed');
        zoomButton.setAttribute('aria-pressed', 'true');
        zoomButton.setAttribute('aria-label', zoomButton.dataset.exitLabel);
        zoomButton.querySelector('.visually-hidden').textContent = zoomButton.dataset.exitLabel;
        this.zoomImage.src = zoomImage.currentSrc || zoomImage.getAttribute('src');
        this.zoomImage.width = zoomImage.naturalWidth || Math.round(this.zoomBounds.width);
        this.zoomImage.height = zoomImage.naturalHeight || Math.round(this.zoomBounds.height);
        this.updateZoomImageLayout();
        this.zoomRoot.hidden = false;
        this.zoomRoot.classList.remove('is-active', 'is-closing');
        this.applyZoomTransform({ immediate: true });
        zoomImage.classList.add('is-scoreggia-elevated-zoom-source');
        this.autoplay.clear();
        this.announceZoomState(zoomButton.dataset.exitLabel);
        this.zoomOpenFrame = window.requestAnimationFrame(() => {
          this.zoomOpenFrame = null;
          if (this.zoomed) this.zoomRoot?.classList.add('is-active');
        });
      }

      getRenderedImageBounds(image) {
        const bounds = image?.getBoundingClientRect();
        if (!bounds?.width || !bounds?.height) return null;
        const computed = getComputedStyle(image);
        return window.ScoreggiaMediaCore.containedImageBounds({
          bounds: { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height },
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          objectFit: computed.objectFit,
          objectPosition: computed.objectPosition,
        });
      }

      getCenteredZoomBounds(bounds) {
        if (!bounds) return null;
        const viewport = this.getZoomViewport();
        return window.ScoreggiaMediaCore.centerBoundsInViewport({
          bounds,
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
          viewportLeft: viewport.left,
          viewportTop: viewport.top,
        });
      }

      getZoomViewport() {
        const viewport = window.visualViewport;
        return {
          width: Math.max(viewport?.width || window.innerWidth || 0, 1),
          height: Math.max(viewport?.height || window.innerHeight || 0, 1),
          left: viewport?.offsetLeft || 0,
          top: viewport?.offsetTop || 0,
        };
      }

      updateZoomImageLayout() {
        if (!this.zoomImage || !this.zoomBounds) return;
        this.zoomImage.style.left = `${this.zoomBounds.left}px`;
        this.zoomImage.style.top = `${this.zoomBounds.top}px`;
        this.zoomImage.style.width = `${this.zoomBounds.width}px`;
        this.zoomImage.style.height = `${this.zoomBounds.height}px`;
      }

      zoomSourceIsCurrent() {
        if (!this.zoomSourceImage?.isConnected) return false;
        if (!this.zoomSourceSlide) return true;
        const activeSlide = this.controller?.slides?.[this.controller.activeIndex];
        return (
          this.zoomSourceSlide.isConnected &&
          activeSlide === this.zoomSourceSlide &&
          !this.zoomSourceSlide.hasAttribute('data-scoreggia-loop-clone') &&
          (!this.zoomSourceMediaId || this.zoomSourceSlide.dataset.mediaId === this.zoomSourceMediaId) &&
          (!this.zoomSourceSlide.contains || this.zoomSourceSlide.contains(this.zoomSourceImage))
        );
      }

      refreshZoomSourceGeometry({ resetOnFailure = true } = {}) {
        if (!this.zoomSourceIsCurrent()) {
          if (resetOnFailure) this.resetZoom({ immediate: true, restoreFocus: true });
          return false;
        }
        const sourceBounds = this.getRenderedImageBounds(this.zoomSourceImage);
        const centeredBounds = this.getCenteredZoomBounds(sourceBounds);
        if (
          !sourceBounds ||
          !centeredBounds ||
          ![
            sourceBounds.left,
            sourceBounds.top,
            sourceBounds.width,
            sourceBounds.height,
            centeredBounds.left,
            centeredBounds.top,
          ].every(Number.isFinite)
        ) {
          if (resetOnFailure) this.resetZoom({ immediate: true, restoreFocus: true });
          return false;
        }
        this.zoomSourceBounds = sourceBounds;
        this.zoomCenteredBounds = centeredBounds;
        return true;
      }

      refreshZoomBounds() {
        if (!this.zoomed) return;
        if (!this.refreshZoomSourceGeometry()) return;
        this.applyZoomTransform({ immediate: true });
        this.restartZoomManipulation();
      }

      announceZoomState(message) {
        if (!this.liveRegion || !message) return;
        if (this.zoomAnnouncementTimer !== null) window.clearTimeout(this.zoomAnnouncementTimer);
        this.liveRegion.setAttribute('aria-hidden', 'false');
        this.liveRegion.textContent = message;
        this.zoomAnnouncementTimer = window.setTimeout(() => {
          this.liveRegion?.setAttribute('aria-hidden', 'true');
          this.zoomAnnouncementTimer = null;
        }, 2000);
      }

      resetZoom({ immediate = false, restoreFocus = false } = {}) {
        if (this.zoomCloseTimer !== null) {
          window.clearTimeout(this.zoomCloseTimer);
          this.zoomCloseTimer = null;
        }
        if (this.zoomOpenFrame !== null) {
          window.cancelAnimationFrame(this.zoomOpenFrame);
          this.zoomOpenFrame = null;
        }
        this.clearZoomClickSuppression();
        this.cancelZoomManipulation();
        this.clearZoomWheelState();
        this.clearZoomTransformFrame();
        const layerWasVisible = Boolean(this.zoomRoot && !this.zoomRoot.hidden);
        this.zoomed = false;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.classList.remove('is-zoomed');
        document.documentElement.classList.remove('scoreggia-elevated-zoom-open');
        this.querySelectorAll('.scoreggia-product-media__slide.is-zoomed').forEach((slide) => {
          slide.classList.remove('is-zoomed');
        });
        this.querySelectorAll('[data-scoreggia-zoom]').forEach((button) => {
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', button.dataset.zoomLabel);
          const label = button.querySelector('.visually-hidden');
          if (label) label.textContent = button.dataset.zoomLabel;
        });
        this.zoomRoot?.classList.remove('is-active');
        this.zoomRoot?.classList.toggle('is-closing', layerWasVisible && !immediate);

        if (!layerWasVisible || immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishZoomClose({ restoreFocus });
          return;
        }
        this.zoomCloseTimer = window.setTimeout(() => this.finishZoomClose({ restoreFocus }), 140);
      }

      finishZoomClose({ restoreFocus = false } = {}) {
        if (this.zoomCloseTimer !== null) window.clearTimeout(this.zoomCloseTimer);
        this.zoomCloseTimer = null;
        if (this.zoomAnnouncementTimer !== null) window.clearTimeout(this.zoomAnnouncementTimer);
        this.zoomAnnouncementTimer = null;
        this.zoomRoot?.classList.remove('is-active', 'is-closing');
        if (this.zoomRoot) this.zoomRoot.hidden = true;
        if (this.zoomImage) {
          this.zoomImage.removeAttribute('src');
          this.zoomImage.removeAttribute('style');
        }
        this.zoomSourceImage?.classList.remove('is-scoreggia-elevated-zoom-source');
        const focusTarget = this.zoomSourceButton;
        this.zoomSourceImage = null;
        this.zoomSourceButton = null;
        this.zoomSourceSlide = null;
        this.zoomSourceMediaId = null;
        this.zoomSourceBounds = null;
        this.zoomCenteredBounds = null;
        this.zoomBounds = null;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        if (restoreFocus && focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      }

      applyZoomTransform({ immediate = false } = {}) {
        if (!this.zoomImage || !this.zoomed) return;
        this.zoomScale = window.ScoreggiaMediaCore.clampScale(
          this.zoomScale,
          this.zoomMinimumScale,
          this.zoomMaximumScale,
        );
        const viewport = this.getZoomViewport();
        const desktopReturn =
          this.zoomReturnMode === 'desktop-wheel' && this.zoomSourceBounds && this.zoomCenteredBounds;
        let requestedPanX = this.panX;
        let requestedPanY = this.panY;
        let panDamping = 1;
        if (desktopReturn) {
          const geometry = window.ScoreggiaMediaCore.desktopZoomReturnGeometry({
            scale: this.zoomScale,
            initialScale: this.zoomInitialScale,
            sourceBounds: this.zoomSourceBounds,
            viewportWidth: viewport.width,
            viewportHeight: viewport.height,
            viewportLeft: viewport.left,
            viewportTop: viewport.top,
            userPanX: this.panX,
            userPanY: this.panY,
          });
          if (!geometry) {
            this.resetZoom({ immediate: true, restoreFocus: true });
            return;
          }
          this.zoomBounds = geometry.bounds;
          this.zoomReturnProgress = geometry.returnProgress;
          requestedPanX = geometry.panX;
          requestedPanY = geometry.panY;
          panDamping = geometry.panDamping;
        } else {
          this.zoomBounds = this.zoomCenteredBounds || this.zoomBounds;
          this.zoomReturnProgress = 0;
        }
        if (!this.zoomBounds) return;
        this.updateZoomImageLayout();
        const atDesktopMinimum = desktopReturn && this.zoomScale <= this.zoomMinimumScale + 0.001;
        const panConstraints = {
          panX: atDesktopMinimum ? 0 : requestedPanX,
          panY: atDesktopMinimum ? 0 : requestedPanY,
          bounds: this.zoomBounds,
          scale: this.zoomScale,
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
          viewportLeft: viewport.left,
          viewportTop: viewport.top,
          strictBounds: true,
          allowFittedTravel: true,
        };
        if (desktopReturn && this.zoomScale <= this.zoomInitialScale) panConstraints.strictBounds = false;
        const constrained = window.ScoreggiaMediaCore.clampElevatedPan(panConstraints);
        this.zoomRenderedPanX = atDesktopMinimum ? 0 : constrained.x;
        this.zoomRenderedPanY = atDesktopMinimum ? 0 : constrained.y;
        if (desktopReturn) {
          if (atDesktopMinimum) {
            this.panX = 0;
            this.panY = 0;
          } else if (panDamping > 0.000001) {
            this.panX = constrained.x / panDamping;
            this.panY = constrained.y / panDamping;
          }
        } else {
          this.panX = constrained.x;
          this.panY = constrained.y;
        }
        if (immediate) {
          this.clearZoomTransformFrame();
          this.writeZoomTransform();
          return;
        }
        if (this.zoomTransformFrame !== null) return;
        this.zoomTransformFrame = window.requestAnimationFrame(() => {
          this.zoomTransformFrame = null;
          this.writeZoomTransform();
        });
      }

      writeZoomTransform() {
        if (!this.zoomImage || !this.zoomed) return;
        this.zoomImage.style.setProperty('--scoreggia-zoom-x', `${this.zoomRenderedPanX}px`);
        this.zoomImage.style.setProperty('--scoreggia-zoom-y', `${this.zoomRenderedPanY}px`);
        this.zoomImage.style.setProperty('--scoreggia-zoom-scale', String(this.zoomScale));
      }

      clearZoomTransformFrame() {
        if (this.zoomTransformFrame !== null) window.cancelAnimationFrame(this.zoomTransformFrame);
        this.zoomTransformFrame = null;
      }

      handleZoomDismissal(event) {
        if (!this.zoomed) return;
        event.preventDefault();
        event.stopPropagation();
        this.stopAutoplayAfterInteraction('outside-zoom');
        this.resetZoom({ restoreFocus: true });
      }

      handleZoomImageClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.suppressNextZoomClick) {
          this.clearZoomClickSuppression();
          return;
        }
        this.stopAutoplayAfterInteraction('zoom-exit');
        this.resetZoom({ restoreFocus: true });
      }

      handleZoomWheel(event) {
        if (!this.zoomed || !this.zoomBounds) return;
        const viewport = this.getZoomViewport();
        const deltaY = window.ScoreggiaMediaCore.normalizeWheelDelta({
          deltaY: event.deltaY,
          deltaMode: event.deltaMode,
          lineHeight: 16,
          pageHeight: viewport.height,
        });
        if (!deltaY) return;
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        this.stopAutoplayAfterInteraction('zoom-wheel');
        this.zoomImage?.classList.add('is-wheel-manipulating');
        if (this.zoomWheelIdleTimer !== null) {
          window.clearTimeout(this.zoomWheelIdleTimer);
          this.zoomWheelIdleTimer = null;
        }
        this.zoomWheelSamples.push({
          deltaY,
        });
        if (this.zoomWheelFrame !== null) return;
        this.zoomWheelFrame = window.requestAnimationFrame(() => {
          this.zoomWheelFrame = null;
          this.processZoomWheelSamples();
        });
      }

      processZoomWheelSamples() {
        if (!this.zoomed || !this.zoomBounds) {
          this.clearZoomWheelState();
          return;
        }
        if (!this.refreshZoomSourceGeometry()) return;
        this.zoomReturnMode = 'desktop-wheel';
        this.zoomWheelSamples.splice(0).forEach((sample) => {
          const nextScale = window.ScoreggiaMediaCore.wheelScaleFromDelta({
            scale: this.zoomScale,
            deltaY: sample.deltaY,
            minimumScale: this.zoomMinimumScale,
            maximumScale: this.zoomMaximumScale,
          });
          this.zoomScale = nextScale;
        });
        if (this.zoomScale <= this.zoomMinimumScale + 0.001) {
          this.zoomScale = this.zoomMinimumScale;
          if (!this.refreshZoomSourceGeometry()) return;
        }
        this.applyZoomTransform({ immediate: true });
        this.zoomWheelIdleTimer = window.setTimeout(() => {
          this.zoomWheelIdleTimer = null;
          this.zoomImage?.classList.remove('is-wheel-manipulating');
          if (this.zoomed && this.zoomScale <= this.zoomMinimumScale + 0.001) {
            this.zoomScale = this.zoomMinimumScale;
            this.resetZoom({ restoreFocus: true });
          }
        }, 160);
      }

      clearZoomWheelState() {
        if (this.zoomWheelFrame !== null) window.cancelAnimationFrame(this.zoomWheelFrame);
        if (this.zoomWheelIdleTimer !== null) window.clearTimeout(this.zoomWheelIdleTimer);
        this.zoomWheelFrame = null;
        this.zoomWheelIdleTimer = null;
        this.zoomWheelSamples = [];
        this.zoomImage?.classList.remove('is-wheel-manipulating');
      }

      handleZoomPointerDown(event) {
        if (!this.zoomed || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (this.zoomPointers.has(event.pointerId)) return;
        if (event.pointerType && event.pointerType !== 'mouse' && this.zoomReturnMode === 'desktop-wheel') {
          this.zoomReturnMode = null;
          this.zoomBounds = this.zoomCenteredBounds || this.zoomBounds;
          this.applyZoomTransform({ immediate: true });
        }
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        if (this.zoomPointers.size === 0) {
          this.clearZoomClickSuppression();
          this.zoomManipulated = false;
        }
        const pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          pointerType: event.pointerType || 'pointer',
          captured: false,
        };
        this.zoomPointers.set(event.pointerId, pointer);
        this.captureZoomPointer(pointer);
        this.bindZoomPointerListeners();
        this.restartZoomManipulation();
        this.stopAutoplayAfterInteraction('zoom-manipulation');
      }

      handleZoomPointerMove(event) {
        const pointer = this.zoomPointers.get(event.pointerId);
        if (!pointer || !this.zoomed) return;
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        if (event.cancelable) event.preventDefault();

        if (this.zoomPointers.size >= 2) {
          const pinchPointers = this.zoomPinchPointerIds.map((id) => this.zoomPointers.get(id)).filter(Boolean);
          if (this.zoomMode !== 'pinch' || pinchPointers.length < 2) {
            this.beginZoomPinch();
            return;
          }
          const geometry = window.ScoreggiaMediaCore.pointerPairGeometry(pinchPointers);
          if (!geometry || !this.zoomPinchStart) return;
          const transform = window.ScoreggiaMediaCore.pinchTransformFromStart({
            startScale: this.zoomPinchStart.scale,
            startPanX: this.zoomPinchStart.panX,
            startPanY: this.zoomPinchStart.panY,
            startDistance: this.zoomPinchStart.distance,
            currentDistance: geometry.distance,
            startMidpoint: this.zoomPinchStart.midpoint,
            currentMidpoint: geometry.midpoint,
            center: {
              x: this.zoomBounds.left + this.zoomBounds.width / 2,
              y: this.zoomBounds.top + this.zoomBounds.height / 2,
            },
            minimumScale: this.zoomMinimumScale,
            maximumScale: this.zoomMaximumScale,
          });
          if (
            Math.abs(transform.scale - this.zoomPinchStart.scale) > 0.001 ||
            Math.hypot(
              geometry.midpoint.x - this.zoomPinchStart.midpoint.x,
              geometry.midpoint.y - this.zoomPinchStart.midpoint.y,
            ) > 0.5
          ) {
            this.markZoomManipulated();
          }
          this.zoomScale = transform.scale;
          this.panX = transform.x;
          this.panY = transform.y;
          this.applyZoomTransform();
          return;
        }

        if (this.zoomMode !== 'pan' || !this.zoomPanStart) this.beginZoomPan(pointer);
        const deltaX = pointer.x - this.zoomPanStart.pointerX;
        const deltaY = pointer.y - this.zoomPanStart.pointerY;
        if (Math.hypot(deltaX, deltaY) > 5) this.markZoomManipulated();
        this.panX = this.zoomPanStart.panX + deltaX;
        this.panY = this.zoomPanStart.panY + deltaY;
        this.applyZoomTransform();
      }

      handleZoomPointerEnd(event) {
        this.finishZoomPointer(event.pointerId, false);
      }

      handleZoomPointerCancel(event) {
        this.finishZoomPointer(event.pointerId, true);
      }

      finishZoomPointer(pointerId, canceled) {
        const pointer = this.zoomPointers.get(pointerId);
        if (!pointer) return;
        const completedPinch = this.zoomMode === 'pinch';
        this.releaseZoomPointer(pointer);
        this.zoomPointers.delete(pointerId);
        if (completedPinch && this.zoomScale <= this.zoomMinimumScale + 0.001) {
          this.zoomScale = this.zoomMinimumScale;
          this.applyZoomTransform({ immediate: true });
          this.resetZoom({ restoreFocus: true });
          return;
        }
        if (this.zoomPointers.size > 0) {
          this.restartZoomManipulation();
          return;
        }
        this.clearZoomPointerListeners();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomImage?.classList.remove('is-manipulating');
        if (this.zoomManipulated) this.markZoomClickForSuppression();
        this.zoomManipulated = false;
        if (
          canceled &&
          ![this.zoomScale, this.panX, this.panY].every(Number.isFinite)
        ) {
          this.resetZoom({ immediate: true, restoreFocus: true });
        }
      }

      restartZoomManipulation() {
        const pointers = Array.from(this.zoomPointers.values());
        if (pointers.length >= 2) this.beginZoomPinch();
        else if (pointers.length === 1) this.beginZoomPan(pointers[0]);
        else {
          this.zoomMode = null;
          this.zoomPanStart = null;
          this.zoomPinchStart = null;
          this.zoomPinchPointerIds = [];
        }
      }

      beginZoomPan(pointer) {
        if (!pointer) return;
        this.zoomMode = 'pan';
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomPanStart = {
          pointerX: pointer.x,
          pointerY: pointer.y,
          panX: this.panX,
          panY: this.panY,
        };
      }

      beginZoomPinch() {
        const pointers = Array.from(this.zoomPointers.values()).slice(0, 2);
        const geometry = window.ScoreggiaMediaCore.pointerPairGeometry(pointers);
        if (!geometry || geometry.distance <= 0) return;
        this.zoomMode = 'pinch';
        this.zoomPanStart = null;
        this.zoomPinchPointerIds = pointers.map((pointer) => pointer.id);
        this.zoomPinchStart = {
          distance: geometry.distance,
          midpoint: geometry.midpoint,
          scale: this.zoomScale,
          panX: this.panX,
          panY: this.panY,
        };
      }

      markZoomManipulated() {
        this.zoomManipulated = true;
        this.zoomImage?.classList.add('is-manipulating');
        this.stopAutoplayAfterInteraction('zoom-manipulation');
      }

      captureZoomPointer(pointer) {
        if (!pointer || !this.zoomImage?.setPointerCapture) return;
        try {
          this.zoomImage.setPointerCapture(pointer.id);
          pointer.captured = this.zoomImage.hasPointerCapture ? this.zoomImage.hasPointerCapture(pointer.id) : true;
        } catch {
          pointer.captured = false;
        }
      }

      releaseZoomPointer(pointer) {
        if (!pointer?.captured || !this.zoomImage?.releasePointerCapture) return;
        let captured = pointer.captured;
        if (this.zoomImage.hasPointerCapture) {
          try {
            captured = this.zoomImage.hasPointerCapture(pointer.id);
          } catch {
            captured = false;
          }
        }
        if (!captured) return;
        try {
          this.zoomImage.releasePointerCapture(pointer.id);
        } catch {
          return;
        }
      }

      bindZoomPointerListeners() {
        if (this.activeZoomPointerListeners) return;
        const move = (event) => this.handleZoomPointerMove(event);
        const end = (event) => this.handleZoomPointerEnd(event);
        const cancel = (event) => this.handleZoomPointerCancel(event);
        window.addEventListener('pointermove', move, { passive: false });
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', cancel);
        this.activeZoomPointerListeners = { move, end, cancel };
      }

      clearZoomPointerListeners() {
        const listeners = this.activeZoomPointerListeners;
        if (!listeners) return;
        window.removeEventListener('pointermove', listeners.move);
        window.removeEventListener('pointerup', listeners.end);
        window.removeEventListener('pointercancel', listeners.cancel);
        this.activeZoomPointerListeners = null;
      }

      markZoomClickForSuppression() {
        this.clearZoomClickSuppression();
        this.suppressNextZoomClick = true;
        this.zoomClickTimer = window.setTimeout(() => this.clearZoomClickSuppression(), 500);
      }

      clearZoomClickSuppression() {
        if (this.zoomClickTimer !== null) window.clearTimeout(this.zoomClickTimer);
        this.zoomClickTimer = null;
        this.suppressNextZoomClick = false;
      }

      cancelZoomManipulation() {
        this.zoomPointers.forEach((pointer) => this.releaseZoomPointer(pointer));
        this.zoomPointers.clear();
        this.clearZoomPointerListeners();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomManipulated = false;
        this.zoomImage?.classList.remove('is-manipulating');
      }

      get activeMediaBusy() {
        const activeSlide = this.controller?.slides[this.controller.activeIndex];
        if (!activeSlide) return false;
        const playingVideo = Array.from(activeSlide.querySelectorAll('video')).some((video) => !video.paused);
        return (
          playingVideo ||
          Boolean(activeSlide.querySelector('deferred-media[loaded], product-model[loaded], iframe:focus, model-viewer:focus'))
        );
      }
    },
  );
}
